import React from "react";
import PropTypes from "prop-types";
import { Link, graphql } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import Seo from "../components/Seo";
import StravaIframe from "../components/StravaIframe";
import Layout from "../components/Layout";
import Content, { HTMLContent } from "../components/Content";
import HighlightImages from "../components/HighlightImages";
import TrailFullWidthImage from "../components/TrailFullWidthImage";
import DisplayContentHeader from "../components/DisplayContentHeader";
import TrailDetails from "../components/TrailDetails";
import { nanoid } from "nanoid";
import TrailBlock from "../components/TrailBlock";
import TrailDetailsBasic from "../components/TrailDetailsBasic";
import Highlights from "../components/Highlights";
import TestimonialsSliderNoLogo from "../components/TestimonialsSliderNoLogo";
import Button from "../components/Button";

// eslint-disable-next-line
export const FullWidthPageTemplate = ({
  title,
  subtitle,
  image,
  meta,
  gallery,
  body,
  helmet,
  trail,
  highlights,
  gallerySlug,
  slug,
  pickups,
  price,
  departureData,
  testimonials,
  faq,
  pageTestimonials,
  config,
}) => {
  const PageContent = HTMLContent || Content;
  const heroImage = getImage(image) || image;

  const showTitle = meta.showTitle;

  const stravaIframeURL = trail.stravaIframeURL;
  let bookingSlugs = [];

  bookingSlugs["Slickrock"] =
    "https://www.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/RWqEk";
  bookingSlugs["The Whole Enchilada"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/JyNk8";
  bookingSlugs["The Whole Enchilada Lower Section"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/JyNk8";
  bookingSlugs["Mag 7"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/qzZx0";
  bookingSlugs["Porcupine Rim Classic"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/JyNk8";
  bookingSlugs["Raptor Route"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/XDdRv";
  bookingSlugs["Moab Brands / Bar-M"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/DxNy1";

  bookingSlugs["San Juan Huts"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/jPZYp";

  bookingSlugs["Labyrinth Canyon"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/zqZ7Y--714a88ae-d8af-48f7-8844-b63fc7758cd6";
  bookingSlugs["Cataract Canyon"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/k42Yy--8c0aa9f8-0545-4f6a-8cb7-ec0f0838642b";
  bookingSlugs["Guided MTB Tour"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/emwRr--4626f750-f448-40ea-9647-d0465b73e392";

  bookingSlugs["Raptor Route Eagle Eye - Hawks Glide - Falcon Flow"] =
    bookingSlugs["Raptor Route"];
  bookingSlugs["Mag-7"] = bookingSlugs["Mag 7"];

  bookingSlugs["Kokopelli"] =
    "https://book.peek.com/s/6859bbed-0689-4c50-9592-1fb6e0301ada/qxexP";
  bookingSlugs["Kokopelli Trail"] = bookingSlugs["Kokopelli"];

  title = title.trim();
  let related = false;
  if (trail.related && trail.related.trails) {
    related = trail.related.trails;
  }
  let ticketTitle = title;
  if (trail.note) {
    ticketTitle = ticketTitle + " " + trail.note;
  }
  price = updatePriceFromConfig(price, slug, config);
  if (price && price !== "Price Varies") {
    price = currency(price) + " Per Rider";
  } else {
    price = "Price Varies";
  }

  let bookingSlug = bookingSlugs["Guided MTB Tour"];

  return (
    <div>
      {helmet || null}

      {showTitle ? (
        <TrailFullWidthImage
          img={heroImage}
          title={ticketTitle}
          subheading={subtitle}
          imgPosition="center"
          detail={<TrailDetails details={trail} />}
        />
      ) : (
        <TrailFullWidthImage
          img={heroImage}
          imgPosition="center"
          detail={<TrailDetails details={trail} />}
        />
      )}

      <section className="section section--gradient">
        <div className="container">
          <div className="content">
            <div className="content">
              <DisplayContentHeader
                title={title}
                parent={meta.parent}
                parentSlug={meta.parentSlug}
                grandparent={meta.grandparent}
                grandparentSlug={meta.grandparentSlug}
              />
            </div>

            <div className="columns">
              <div className="column is-6">
                {bookingSlug ? (
                  <div className="content">
                    <a
                      href={bookingSlug}
                      className="peek"
                      data-embed="true"
                      data-button-text="Book Now"
                    >
                      {title}
                    </a>
                  </div>
                ) : null}
                <PageContent
                  className="content content___full-width-page"
                  content={body}
                />
              </div>
              <div className="column pl-4 is-6">
                {stravaIframeURL ? (
                  <StravaIframe url={stravaIframeURL} />
                ) : null}

                {gallery && gallery.images ? (
                  <div className="content">
                    <h2 className="hidden">{title} Images:</h2>
                    <HighlightImages images={gallery.images} />
                  </div>
                ) : null}

                {price ? (
                  <>
                    <div className="content">
                      <h2 className="mb-0 pb-0">{title}</h2>
                      {subtitle ? (
                        <h2 className="mt-0 pt-0">{subtitle}</h2>
                      ) : null}
                      <div style={{ float: "left" }}>
                        <h3>Price:</h3>
                      </div>
                      <div style={{ float: "right" }}>
                        <h3>
                          <span className="price">{price}</span>
                        </h3>
                      </div>
                      <br clear="all" />
                      <hr className="gradient" />
                      <Button
                        title={`Buy Ticket`}
                        link={bookingSlug}
                        className="link-button-orange has-text-white is-size-5 has-text-weight-bold"
                      />
                      <br />
                      <Button
                        title={`Frequently Asked Questions`}
                        link={`/faq/`}
                        className="link-button-orange has-text-white is-size-5 has-text-weight-normal"
                      />
                      {faq ? (
                        <Button
                          title={`Frequently Asked Questions > ${title}`}
                          link={`/faq/${faq}`}
                          className="link-button-orange has-text-white is-size-5 has-text-weight-normal"
                        />
                      ) : null}
                    </div>{" "}
                  </>
                ) : null}
                <div className="content">
                  <h5 className="has-text-centered">
                    Call <a href="tel:+1(435) 210-8082">435-210-8082</a> now to{" "}
                    <Link to="/book-shuttle">book your adventure</Link>, or
                    receive more information.
                  </h5>
                </div>
                {testimonials ? (
                  <TestimonialsSliderNoLogo
                    testimonials={testimonials}
                    pageTestimonials={pageTestimonials}
                    size="is-size-6"
                  />
                ) : null}
              </div>
            </div>
          </div>
          <br clear="all" />
          {highlights ? <Highlights highlights={highlights} /> : null}
        </div>
      </section>

      <section className="section section--gradient light-cammo">
        <div className="black-box" key={nanoid()}>
          <div className="m-4" key={nanoid()}>
            <TrailBlock
              logo={trail.logo}
              title={title}
              display={<TrailDetailsBasic details={trail} />}
              rating={trail.rating}
              slug={""}
              gallerySlug={gallerySlug}
              mapSlug={trail.map}
              videoSlug={trail.videos}
              pickups={pickups}
              departures={departureData}
              related={related}
              price={price}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

FullWidthPageTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  meta: PropTypes.shape({
    showTitle: PropTypes.bool,
    parent: PropTypes.string,
    parentSlug: PropTypes.string,
    grandparent: PropTypes.string,
    grandparentSlug: PropTypes.string,
  }),

  trail: PropTypes.shape({
    logo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    miles: PropTypes.string,
    descent: PropTypes.string,
    elevationGain: PropTypes.string,
    high: PropTypes.string,
    low: PropTypes.string,
    aveGrade: PropTypes.string,
    maxGrade: PropTypes.string,
    stravaIframeURL: PropTypes.string,
    related: PropTypes.shape({
      trails: PropTypes.array,
    }),
  }),
  highlights: PropTypes.shape({
    highlight: PropTypes.array,
  }),

  body: PropTypes.string,
  helmet: PropTypes.object,
};

const FullWidthPage = ({ data }) => {
  //console.log(data);

  const { markdownRemark: post } = data;
  let { departures } = data;
  const { allGoogleRating: testimonials } = data;

  const { frontmatter: config } = data.config;

  let departureData = [];

  departures = departures.edges;

  departures.forEach((departure) => {
    departureData[departure.node.frontmatter.title] = departure.node;
  });

  let imageSrc =
    post.frontmatter.image.childImageSharp.gatsbyImageData.images.fallback
      .src || false;
  let gallerySlug = post.fields.slug;
  gallerySlug = gallerySlug.replace(
    "/guided-mountain-bike-tours",
    "/galleries"
  );

  return (
    <Layout>
      <FullWidthPageTemplate
        title={post.frontmatter.title}
        subtitle={post.frontmatter.subtitle}
        image={post.frontmatter.image}
        meta={post.frontmatter.meta}
        gallery={post.frontmatter.gallery}
        highlights={post.frontmatter.highlights}
        trail={post.frontmatter.trail}
        body={post.html}
        config={config}
        helmet={
          <Seo
            title={post.frontmatter.title}
            summary={post.frontmatter.summary}
            image={imageSrc}
            article={false}
          />
        }
        departureData={departureData}
        slug={post.fields.slug}
        gallerySlug={gallerySlug}
        pickups={
          post.frontmatter.pickups && post.frontmatter.pickups.pickup
            ? post.frontmatter.pickups.pickup
            : false
        }
        faq={post.frontmatter.faq}
        testimonials={testimonials.edges}
        pageTestimonials={post.frontmatter.testimonials}
      />
    </Layout>
  );
};

FullWidthPage.propTypes = {
  data: PropTypes.object.isRequired,
  gallerySlug: PropTypes.string,
  highlights: PropTypes.shape({
    highlight: PropTypes.array,
  }),
  trail: PropTypes.shape({
    logo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    miles: PropTypes.string,
    descent: PropTypes.string,
    elevationGain: PropTypes.string,
    high: PropTypes.string,
    low: PropTypes.string,
    aveGrade: PropTypes.string,
    maxGrade: PropTypes.string,
    stravaIframeURL: PropTypes.string,
    related: PropTypes.shape({
      trails: PropTypes.array,
    }),
  }),
};

export default FullWidthPage;

export const fullWidthPageQuery = graphql`
  query FullWidthPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      fields {
        slug
      }
      frontmatter {
        title
        subtitle
        image {
          childImageSharp {
            gatsbyImageData(
              quality: 100
              layout: FULL_WIDTH
              placeholder: BLURRED
            )
          }
        }
        pickups {
          pickup {
            title
          }
        }
        price
        meta {
          showTitle
          parent
          parentSlug
          grandparent
          grandparentSlug
        }
        summary
        faq
        testimonials {
          testimonial {
            name
            rating
            review
          }
        }
        trail {
          logo {
            childImageSharp {
              gatsbyImageData(
                quality: 100
                layout: FULL_WIDTH
                placeholder: BLURRED
              )
            }
          }
          stravaIframeURL
          videos
          map
          miles
          descent
          elevationGain
          high
          low
          aveGrade
          maxGrade
        }
        gallery {
          images {
            image {
              childImageSharp {
                resize(width: 1920, height: 1080, cropFocus: CENTER) {
                  src
                }
              }
            }
          }
        }
        highlights {
          highlight {
            image {
              childImageSharp {
                gatsbyImageData(
                  quality: 100
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                )
              }
            }
            title
            summary
          }
        }
      }
    }
    departures: allMarkdownRemark(
      sort: { frontmatter: { meta: { sortOrder: ASC } } }
      filter: {
        frontmatter: { templateKey: { eq: "departure-locations-post" } }
      }
    ) {
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            title
            subtitle
            googleMap
          }
        }
      }
    }
    allGoogleRating {
      edges {
        node {
          name
          rating
          review
        }
      }
    }
    config: markdownRemark(
      frontmatter: { templateKey: { eq: "configuration" } }
    ) {
      frontmatter {
        pricing {
          price {
            slug
            price
          }
        }
      }
    }
  }
`;

const currency = function (number) {
  //console.log(number);
  if (
    number !== "Varies" &&
    number !== "Price Varies" &&
    number !== "$NaN" &&
    number !== "$NaN Per Rider" &&
    number
  ) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(number);
  } else {
    return "Price Varies";
  }
};
function updatePriceFromConfig(price, slug, config) {
  //console.log(price, slug, config);
  let pricesArr = [],
    prices = config.pricing.price;

  prices.forEach((element) => {
    pricesArr[element.slug] = element.price;
  });
  if (pricesArr[slug]) {
    return pricesArr[slug];
  } else {
    return price;
  }
}
